/**
 * Service worker registration and connectivity state.
 *
 * The worker itself is generated at build time (scripts/vite-service-worker.ts)
 * so its precache list matches the hashed filenames Vite actually emitted.
 * There is no worker in development — a stale cache is the last thing anyone
 * needs while editing.
 */

export type UpdateHandler = (applyUpdate: () => void) => void;

const SW_URL = '/sw.js';

export function registerServiceWorker(onUpdateReady: UpdateHandler): void {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register(SW_URL, { scope: '/' })
      .then((registration) => {
        // A worker already waiting means the tab was opened on an old build.
        if (registration.waiting) {
          onUpdateReady(() => activate(registration.waiting));
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `controller` is null on the very first install; that is not an
            // update, it is the worker taking over for the first time.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              onUpdateReady(() => activate(installing));
            }
          });
        });
      })
      .catch(() => {
        // An unavailable worker costs offline support, not the app.
      });

    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}

function activate(worker: ServiceWorker | null): void {
  worker?.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Drop cached payroll responses. Called on sign-out: the next person to pick
 * up the phone must not be able to read the last one's hours from the cache.
 */
export async function clearCachedData(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  registration?.active?.postMessage({ type: 'CLEAR_DATA' });
}

/** Subscribe to connectivity changes. Returns an unsubscribe function. */
export function watchConnectivity(onChange: (online: boolean) => void): () => void {
  const update = () => onChange(navigator.onLine);
  window.addEventListener('online', update);
  window.addEventListener('offline', update);
  return () => {
    window.removeEventListener('online', update);
    window.removeEventListener('offline', update);
  };
}

/** True when the app is running from the home screen rather than a browser tab. */
export function isInstalled(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari predates the display-mode media query.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}
